import { makeAutoObservable } from 'mobx';
import { injectable } from 'inversify';
import fileDownload from 'js-file-download';
import { notifications } from '@shared/utils/notifications';
import { type GridSelectionModel, type GridInputSelectionModel, GridRowId } from '@mui/x-data-grid-pro';
import { AppartmentsModule } from '@mobx/services/appartments';
import { ProfileModule } from '@mobx/services/profile';
import { postApiReportsAbonentsInvoicesPdfOnPeriod } from '@shared/api/orval/reports/reports';
import {
  type AppartmentPastInvoicesViewModel,
  type AppartmentPastInvoiceRow
} from '@shared/types/mobx/widgets/AppartmentPastInvoicesWidget';

/**
 * ViewModel вкладки «Квитанции предыдущих периодов» в ЛК жителя.
 * Упрощённый аналог председательского OsiPastInvoicesViewModelImpl:
 * помещения берутся не из запроса по ОСИ, а из уже загруженных данных профиля жителя.
 */
@injectable()
export class AppartmentPastInvoicesViewModelImpl implements AppartmentPastInvoicesViewModel {
  // Выбранный период (месяц+год). По умолчанию — текущая дата.
  date: Date = new Date();

  isLoading = false;

  // Выделенные строки DataGridPro (id = abonentId).
  private _selectionModel: GridInputSelectionModel = [];

  constructor(private appartmentModule: AppartmentsModule, private profileModule: ProfileModule) {
    makeAutoObservable(this);
  }

  /**
   * Помещения текущего жителя в рамках выбранного ОСИ.
   * Фильтр по appartmentModule.osi.id ОБЯЗАТЕЛЕН: бэкенд принимает помещения только одного ОСИ.
   * square приходит с бэкенда (spread полей абонента в profile/loadAbonentData).
   */
  get abonents(): AppartmentPastInvoiceRow[] {
    return this.profileModule.userData.appartments
      .filter((a) => a.osiId === this.appartmentModule.osi?.id)
      .map((a) => ({ id: a.abonentId, flat: a.flat, square: a.square }));
  }

  get selectionModel() {
    return this._selectionModel;
  }

  // true, если не выбрано ни одного помещения → кнопка «Сформировать» блокируется.
  get abonentsIsNotSelected() {
    return !(this._selectionModel as GridRowId[]).length;
  }

  onSelectionModelChange = (model: GridSelectionModel) => {
    this._selectionModel = model;
  };

  /**
   * Формирует PDF с квитанциями за выбранный период по выделенным помещениям.
   * Тело запроса — массив abonentId (selectionModel), параметры — год/месяц выбранной даты.
   */
  downloadInvoices = async () => {
    this.isLoading = true;
    try {
      const year = this.date.getFullYear();
      const month = this.date.getMonth() + 1;

      // responseType: 'blob' — сервер возвращает бинарный PDF (или blob-json с ошибкой).
      const blob = await postApiReportsAbonentsInvoicesPdfOnPeriod(
        this._selectionModel as number[],
        { year, month, isStraightOrder: true, isOnlyResidents: false },
        { responseType: 'blob' }
      );

      fileDownload(blob, `Квитанции за_${year}-${month}.pdf`);
    } catch (error: any) {
      // Сервер возвращает осмысленные ошибки (напр. «за период начисления ещё не производились»)
      // в blob-json — разбираем как в appartmentInvoices/model/viewModel.ts.
      let errorObj = error?.response?.data;
      let message = '';
      if (
        error?.request?.responseType === 'blob' &&
        error?.response?.data instanceof Blob &&
        error?.response?.data.type &&
        error?.response?.data.type.toLowerCase().indexOf('json') !== -1
      ) {
        errorObj = JSON.parse(await error.response.data.text());
        message = errorObj?.message;
      }
      notifications.error(message);
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  };
}
